import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { toast } from '../../lib/dialogs';
import { LoadingBlock } from '../../components/Spinner';
import { formatRange } from '../../lib/eventDates';

/**
 * Admin → Operations → Identify history.
 *
 * Lists every event that the venue identifier (LLM + Google Places helper)
 * has ever attempted, most recent first. Lets the admin audit accuracy of
 * the helper, spot patterns of failure, and re-trigger an identification
 * inline.
 */

interface IdentifyHistoryRow {
  partitionKey: string;
  rowKey: string;
  title: string;
  status: string;
  startUtc: string;
  endUtc?: string;
  isAllDay?: boolean;
  locationText?: string;
  result?: string;
  reason?: string;
  confidence?: number;
  attemptedAt?: string;
  venueId?: string;
  venueName?: string;
}

interface IdentifyHistoryResponse {
  count: number;
  items: IdentifyHistoryRow[];
}

type ResultFilter = 'all' | 'matched' | 'created' | 'low-confidence' | 'no-match' | 'error';

const RESULT_META: Record<string, { label: string; cls: string }> = {
  matched: { label: 'Matched', cls: 'text-bg-success' },
  created: { label: 'Created (pending)', cls: 'text-bg-primary' },
  'low-confidence': { label: 'Low confidence', cls: 'text-bg-warning text-dark' },
  'no-match': { label: 'No match', cls: 'text-bg-secondary' },
  error: { label: 'Error', cls: 'text-bg-danger' },
};

function resultBadge(r?: string): ReactElement {
  if (!r) return <span className="badge text-bg-light text-dark">Unknown</span>;
  const meta = RESULT_META[r] ?? { label: r, cls: 'text-bg-light text-dark' };
  return <span className={`badge ${meta.cls}`}>{meta.label}</span>;
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AdminIdentifyHistoryPage(): ReactElement {
  const [rows, setRows] = useState<IdentifyHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ResultFilter>('all');
  const [search, setSearch] = useState<string>('');
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(() => new Set<string>());

  async function load(): Promise<void> {
    setError(null);
    try {
      const data = await api.get<IdentifyHistoryResponse>('/api/mgmt/events/identify-history?limit=500');
      setRows(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function retry(row: IdentifyHistoryRow): Promise<void> {
    if (retrying.has(row.rowKey)) return;
    setRetrying((prev) => {
      const next = new Set(prev);
      next.add(row.rowKey);
      return next;
    });
    const previousAttempt = row.attemptedAt ?? '';
    try {
      await api.post(
        `/api/mgmt/events/${encodeURIComponent(row.partitionKey)}/${encodeURIComponent(row.rowKey)}/identify-venue`,
        { force: true },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Could not queue identifier: ${msg}`);
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(row.rowKey);
        return next;
      });
      return;
    }
    // Poll the history endpoint for up to 30s for an updated attemptedAt.
    const deadline = Date.now() + 30_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3_000));
      try {
        const data = await api.get<IdentifyHistoryResponse>('/api/mgmt/events/identify-history?limit=500');
        const fresh = data.items.find((r) => r.rowKey === row.rowKey);
        if (fresh && (fresh.attemptedAt ?? '') !== previousAttempt) {
          setRows(data.items);
          break;
        }
      } catch { /* transient */ }
    }
    setRetrying((prev) => {
      const next = new Set(prev);
      next.delete(row.rowKey);
      return next;
    });
  }

  const summary = useMemo(() => {
    const counts: Record<string, number> = { matched: 0, created: 0, 'low-confidence': 0, 'no-match': 0, error: 0 };
    for (const r of rows ?? []) {
      const k = r.result ?? 'unknown';
      counts[k] = (counts[k] ?? 0) + 1;
    }
    counts.total = rows?.length ?? 0;
    counts.success = (counts.matched ?? 0) + (counts.created ?? 0);
    return counts;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== 'all' && r.result !== filter) return false;
      if (q && !`${r.title} ${r.locationText ?? ''} ${r.venueName ?? ''} ${r.reason ?? ''}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rows, filter, search]);

  return (
    <>
      <AdminLayout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h1 className="h3 mb-1">Venue identify history</h1>
          <p className="text-body-secondary small mb-0">
            Audit log of every event the LLM + Google Places helper has attempted, most recent first.
          </p>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void load()}>
          <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
          Refresh
        </button>
      </div>

      {rows && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3 col-lg-2">
            <SummaryCard label="Total" value={summary.total} muted />
          </div>
          <div className="col-6 col-md-3 col-lg-2">
            <SummaryCard label="Matched" value={summary.matched ?? 0} cls="text-bg-success" />
          </div>
          <div className="col-6 col-md-3 col-lg-2">
            <SummaryCard label="Created" value={summary.created ?? 0} cls="text-bg-primary" />
          </div>
          <div className="col-6 col-md-3 col-lg-2">
            <SummaryCard label="Low conf." value={summary['low-confidence'] ?? 0} cls="text-bg-warning text-dark" />
          </div>
          <div className="col-6 col-md-3 col-lg-2">
            <SummaryCard label="No match" value={summary['no-match'] ?? 0} cls="text-bg-secondary" />
          </div>
          <div className="col-6 col-md-3 col-lg-2">
            <SummaryCard label="Errors" value={summary.error ?? 0} cls="text-bg-danger" />
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="Filter by result">
          {(['all', 'matched', 'created', 'low-confidence', 'no-match', 'error'] as ResultFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              className={`btn btn-outline-secondary${filter === f ? ' active' : ''}`}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : RESULT_META[f]?.label ?? f}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: '20rem' }}
          placeholder="Search title, location, venue, reason…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search identify history"
        />
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {rows === null && !error && <LoadingBlock label="Loading identify history…" />}

      {rows && filtered.length === 0 && (
        <p className="text-body-secondary">No identification attempts match your filters.</p>
      )}

      {filtered.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Result</th>
                <th scope="col">Conf.</th>
                <th scope="col">Event</th>
                <th scope="col">Location text</th>
                <th scope="col">Resolved venue</th>
                <th scope="col">Reason</th>
                <th scope="col" className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isRetrying = retrying.has(r.rowKey);
                return (
                  <tr key={`${r.partitionKey}/${r.rowKey}`}>
                    <td>
                      <span title={formatDateTime(r.attemptedAt)}>{timeAgo(r.attemptedAt)}</span>
                    </td>
                    <td>{resultBadge(r.result)}</td>
                    <td>{typeof r.confidence === 'number' ? r.confidence : '—'}</td>
                    <td>
                      <div className="fw-medium text-break">{r.title || '(untitled)'}</div>
                      <div className="small text-body-secondary">
                        {formatRange(r)} · status: {r.status}
                      </div>
                    </td>
                    <td className="small text-break">{r.locationText || <span className="text-body-secondary">—</span>}</td>
                    <td className="small">
                      {r.venueId && r.venueName ? (
                        <Link to={`/venues/${r.venueId}`}>{r.venueName}</Link>
                      ) : (
                        <span className="text-body-secondary">—</span>
                      )}
                    </td>
                    <td className="small text-break">
                      {r.reason || <span className="text-body-secondary">—</span>}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 mh-identify-venue-btn"
                        onClick={() => void retry(r)}
                        disabled={isRetrying}
                        aria-label={`Re-run identifier for ${r.title}`}
                        title={isRetrying ? 'Identifying…' : 'Re-run identifier'}
                      >
                        <i
                          className={`bi ${isRetrying ? 'bi-arrow-repeat mh-spin' : 'bi-search'}`}
                          aria-hidden="true"
                        />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </AdminLayout>
    </>
  );
}

function SummaryCard({ label, value, cls, muted }: { label: string; value: number; cls?: string; muted?: boolean }): ReactElement {
  return (
    <div className={`card border-0 ${muted ? 'bg-body-tertiary' : ''}`}>
      <div className="card-body py-2">
        <div className="small text-body-secondary">{label}</div>
        <div className="d-flex align-items-baseline gap-2">
          <span className="fs-4 fw-semibold">{value}</span>
          {cls && value > 0 && <span className={`badge ${cls}`} aria-hidden="true">&nbsp;</span>}
        </div>
      </div>
    </div>
  );
}
